import React, { FC, useRef, useEffect } from 'react'
import clsx from 'clsx'
import { VoiceItem } from '@/apis/voice'
import LoadingImg from '@/icons/LoadingImg'
import AddPlusCircleIcon from 'Comps/svg/AddPlusCircleIcon'
import useI18n from 'Hooks/useI18n'
import TipsySlider from '@/components/ui-kit/TipsySlider'
import { CloseOutlinedIcon } from '@/components/svg/CloseOutlinedIcon'
import VoicePlayButton, {
  AudioProvider,
} from '@/components/common/VoicePlayButton'

type VoiceItemComponentProps = {
  voice: VoiceItem
  isSelected: boolean
  onSelect: () => void
  variant?: 'default' | 'creatorModal'
}

const CreatorModalVoiceItem: FC<Omit<VoiceItemComponentProps, 'variant'>> = ({
  voice,
  isSelected,
  onSelect,
}) => {
  const { t } = useI18n()

  return (
    <div className="flex h-[76px] shrink-0 items-center justify-between rounded-lg bg-white/5 pr-4">
      <div className="flex min-w-0 flex-1 items-center">
        <div className="flex w-16 shrink-0 items-center justify-center">
          <VoicePlayButton
            voice={voice}
            className="h-8 w-8 shrink-0 rounded-full bg-black/25"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="truncate text-sm font-normal leading-[18px] text-white">
            {voice.name}
          </span>
          <span className="line-clamp-1 text-xs leading-4 text-white/30">
            {voice.description}
          </span>
        </div>
      </div>

      <button
        type="button"
        className={clsx(
          'ml-4 flex h-8 w-[100px] shrink-0 items-center justify-center rounded-full text-[13px] leading-[13px] transition-colors duration-150 active:scale-95',
          isSelected
            ? 'cursor-default bg-[#9C4844]/20 text-white/30'
            : 'bg-[#9C4844] text-white hover:bg-[#b45450]'
        )}
        onClick={onSelect}
        disabled={isSelected}
      >
        {isSelected ? t('In use') : t('Choose')}
      </button>
    </div>
  )
}

const DefaultVoiceItem: FC<Omit<VoiceItemComponentProps, 'variant'>> = ({
  voice,
  onSelect,
}) => {
  return (
    <div
      className={clsx(
        'flex items-center justify-between border-b border-white/5 py-4'
      )}
    >
      <div className="flex items-center">
        {/* 语音图标*/}
        <VoicePlayButton voice={voice} className="mr-3 self-start" />

        {/* 文本内容 */}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-white/90">
            {voice.name}
          </span>
          <span className="line-clamp-1 text-xs text-[#9E9E9E]/70">
            {voice.description}
          </span>
        </div>
      </div>

      {/* 添加按钮 */}
      <button
        className="flex h-5 w-5 transform items-center justify-center rounded-full bg-white/5 transition-colors duration-150 active:scale-95 active:bg-white/20"
        onClick={onSelect}
      >
        <AddPlusCircleIcon size={16} color="white" strokeWidth={1.5} />
      </button>
    </div>
  )
}

// 声音项组件
export const VoiceItemComponent: FC<VoiceItemComponentProps> = ({
  voice,
  isSelected,
  onSelect,
  variant = 'default',
}) => {
  if (variant === 'creatorModal') {
    return (
      <CreatorModalVoiceItem
        voice={voice}
        isSelected={isSelected}
        onSelect={onSelect}
      />
    )
  }

  return (
    <DefaultVoiceItem
      voice={voice}
      isSelected={isSelected}
      onSelect={onSelect}
    />
  )
}

type SelectedVoiceItemProps = {
  voice: VoiceItem
  onRemove: () => void
  onSpeedChange: (value: number) => void
  speed: number
  compact?: boolean
}

const CompactSelectedVoiceItem: FC<Omit<SelectedVoiceItemProps, 'compact'>> = ({
  voice,
  onRemove,
  onSpeedChange,
  speed,
}) => {
  const { t } = useI18n()

  return (
    <div className="flex w-full min-w-0 flex-col">
      <div className="flex min-w-0 items-center">
        <div className="flex w-12 shrink-0 items-center justify-center">
          <VoicePlayButton
            voice={voice}
            speed={speed}
            className="h-8 w-8 shrink-0 rounded-full bg-black/25"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="truncate text-sm font-normal leading-[18px] text-white">
            {voice.name}
          </span>
          <span className="line-clamp-1 text-xs leading-4 text-white/30">
            {voice.description}
          </span>
        </div>
        <button
          type="button"
          className="ml-4 shrink-0 rounded-full bg-white/5 px-3 py-1 text-xs text-white/50 transition-colors hover:bg-white/10 hover:text-white"
          onClick={onRemove}
        >
          {t('Delete')}
        </button>
      </div>

      <div className="mt-4 flex items-center">
        <span className="mr-3 w-12 text-xs leading-4 text-white/60">
          {t('Speed')}
        </span>
        <span className="mr-2 w-6 text-xs text-white/30">0.1</span>
        <TipsySlider
          className="flex-1"
          height={6}
          min={0.1}
          max={3.0}
          step={0.1}
          value={speed}
          onChange={onSpeedChange}
        />
        <span className="ml-2 w-6 text-xs text-white/30">3.0</span>
        <span className="ml-3 w-9 text-right text-xs text-white/60">
          {speed}x
        </span>
      </div>
    </div>
  )
}

const DefaultSelectedVoiceItem: FC<Omit<SelectedVoiceItemProps, 'compact'>> = ({
  voice,
  onRemove,
  onSpeedChange,
  speed,
}) => {
  const { t } = useI18n()

  return (
    <div className="flex flex-col rounded-lg bg-white/5 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {/* 语音图标 */}
          <VoicePlayButton voice={voice} speed={speed} className="mr-3" />

          {/* 文本内容 */}
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-white/90">
              {voice.name}
            </span>
            <span className="line-clamp-1 text-xs text-[#9E9E9E]/70">
              {voice.description}
            </span>
          </div>
        </div>

        {/* 关闭按钮 */}
        <button
          className="flex h-6 w-6 items-center justify-center rounded-full bg-white/5"
          onClick={onRemove}
        >
          <CloseOutlinedIcon size={16} strokeWidth={2.5} />
        </button>
      </div>

      {/* 速度滑块 */}
      <div className="mt-4 flex items-center">
        <span className="mr-2 w-12 whitespace-normal break-words text-sm text-white">
          {t('Speed')}
        </span>
        <span className="mr-2 w-5 text-white/30">0.1</span>
        <TipsySlider
          className="flex-1"
          height={6}
          min={0.1}
          max={3.0}
          step={0.1}
          defaultValue={speed}
          onChange={onSpeedChange}
        />
        <span className="ml-2 w-5 text-white/30">3.0</span>
      </div>
    </div>
  )
}

// 已选中的声音组件
export const SelectedVoiceItem: FC<SelectedVoiceItemProps> = ({
  voice,
  onRemove,
  onSpeedChange,
  speed,
  compact = false,
}) => {
  if (compact) {
    return (
      <CompactSelectedVoiceItem
        voice={voice}
        onRemove={onRemove}
        onSpeedChange={onSpeedChange}
        speed={speed}
      />
    )
  }

  return (
    <DefaultSelectedVoiceItem
      voice={voice}
      onRemove={onRemove}
      onSpeedChange={onSpeedChange}
      speed={speed}
    />
  )
}

// 加载更多触发器
export const LoadMoreTrigger: FC<{ loading: boolean }> = ({ loading }) => {
  const { t } = useI18n()

  return (
    <div className="flex h-6 items-center justify-center">
      {loading ? (
        <LoadingImg className="scale-[.5]" />
      ) : (
        <div className="text-sm text-white/30">{t('Load More')}</div>
      )}
    </div>
  )
}

// 主组件
const VoiceList: FC<{
  voices: VoiceItem[]
  selectedVoice: VoiceItem | null
  onSelectVoice: (voice: VoiceItem) => void
  isLoading: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
  hasMore: boolean
  variant?: 'default' | 'creatorModal'
}> = ({
  voices,
  selectedVoice,
  onSelectVoice,
  isLoading,
  isLoadingMore,
  onLoadMore,
  hasMore,
  variant = 'default',
}) => {
  const { t } = useI18n()
  const listEndRef = useRef<HTMLDivElement>(null)

  // 监听滚动，触发加载更多
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore
        ) {
          onLoadMore()
        }
      },
      { threshold: 0.5 }
    )

    const currentRef = listEndRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [hasMore, isLoading, isLoadingMore, onLoadMore])

  return (
    <AudioProvider>
      <div className="flex h-full w-full flex-col">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <LoadingImg className="scale-[.5]" />
          </div>
        ) : voices.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-white/50">
            {t('No voices found')}
          </div>
        ) : (
          <div
            className={clsx(
              'overflow-y-auto',
              variant === 'creatorModal' && 'flex h-full min-h-0 flex-col gap-3'
            )}
          >
            {voices.map((voice) => (
              <VoiceItemComponent
                key={voice.voice_id}
                voice={voice}
                isSelected={selectedVoice?.voice_id === voice.voice_id}
                onSelect={() => onSelectVoice(voice)}
                variant={variant}
              />
            ))}
            {hasMore && (
              <div ref={listEndRef}>
                <LoadMoreTrigger loading={isLoadingMore} />
              </div>
            )}
          </div>
        )}
      </div>
    </AudioProvider>
  )
}

export default VoiceList
